// Jalaali Date Picker Component
import { toPersianNumbers } from './jalaali-utils'

// Embedded jalaali functions for the picker
const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210,
    1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178]

function div(a: number, b: number): number { return ~~(a / b) }
function mod(a: number, b: number): number { return a - ~~(a / b) * b }

function g2d(gy: number, gm: number, gd: number): number {
    let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408
    d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752
    return d
}

function jalCal(jy: number): { leap: number, gy: number, march: number } {
    const bl = breaks.length; let gy = jy + 621, leapJ = -14, jp = breaks[0], jm, jump: number = 0, n, i
    for (i = 1; i < bl; i++) { jm = breaks[i]; jump = jm - jp; if (jy < jm) break; leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4); jp = jm }
    n = jy - jp; leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4)
    if (mod(jump, 33) === 4 && jump - n === 4) leapJ++
    const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150, march = 20 + leapJ - leapG
    if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33
    let leap = mod(mod(n + 1, 33) - 1, 4); if (leap === -1) leap = 4
    return { leap, gy, march }
}

function j2d(jy: number, jm: number, jd: number): number {
    const r = jalCal(jy); return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1
}

function d2g(jdn: number): { gy: number, gm: number, gd: number } {
    let j = 4 * jdn + 139361631; j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908
    const i = div(mod(j, 1461), 4) * 5 + 308
    return { gd: div(mod(i, 153), 5) + 1, gm: mod(div(i, 153), 12) + 1, gy: div(j, 1461) - 100100 + div(8 - mod(div(i, 153), 12) - 1, 6) }
}

function d2j(jdn: number): { jy: number, jm: number, jd: number } {
    const gy = d2g(jdn).gy; let jy = gy - 621; const r = jalCal(jy), jdn1f = g2d(gy, 3, r.march); let k = jdn - jdn1f
    if (k >= 0) { if (k <= 185) return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 }; k -= 186 }
    else { jy--; k += 179; if (r.leap === 1) k++ }
    return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 }
}

function toJalaali(date: Date): { jy: number, jm: number, jd: number } {
    return d2j(g2d(date.getFullYear(), date.getMonth() + 1, date.getDate()))
}

function toGregorian(jy: number, jm: number, jd: number): Date {
    const g = d2g(j2d(jy, jm, jd)); return new Date(g.gy, g.gm - 1, g.gd)
}

function jalaaliMonthLength(jy: number, jm: number): number {
    if (jm <= 6) return 31; if (jm <= 11) return 30
    const r = jalCal(jy); return r.leap === 0 ? 30 : 29
}

const monthNames = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند']
const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']

export interface JalaaliDatePickerOptions {
    onSelect: (gregorianDate: Date, jalaaliStr: string) => void
}

export function JalaaliDatePicker(options: JalaaliDatePickerOptions) {
    const today = toJalaali(new Date())
    let currentYear = today.jy
    let currentMonth = today.jm
    let selectedDate: { jy: number, jm: number, jd: number } | null = null

    // Container
    const container = document.createElement('div')
    container.style.cssText = 'position:relative;display:inline-block;'

    // Input field
    const input = document.createElement('input')
    input.type = 'text'
    input.readOnly = true
    input.placeholder = 'انتخاب تاریخ'
    input.style.cssText = 'padding:5px 10px;border:1px solid #ccc;border-radius:4px;cursor:pointer;width:120px;text-align:center;direction:rtl;'
    container.appendChild(input)

    // Calendar popup
    const popup = document.createElement('div')
    popup.style.cssText = 'position:absolute;top:100%;right:0;background:#fff;border:1px solid #ccc;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);padding:10px;z-index:1000;display:none;direction:rtl;min-width:280px;'
    container.appendChild(popup)

    function render() {
        const monthLength = jalaaliMonthLength(currentYear, currentMonth)
        const firstDayGreg = toGregorian(currentYear, currentMonth, 1)
        // Saturday = 6 in JS, we want Saturday = 0
        let firstDayOfWeek = (firstDayGreg.getDay() + 1) % 7

        popup.innerHTML = `
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
                <button id="jp-prev" style="border:none;background:#f0f0f0;padding:5px 10px;cursor:pointer;border-radius:4px;">‹</button>
                <span style="font-weight:bold;">${monthNames[currentMonth - 1]} ${toPersianNumbers(String(currentYear))}</span>
                <button id="jp-next" style="border:none;background:#f0f0f0;padding:5px 10px;cursor:pointer;border-radius:4px;">›</button>
            </div>
            <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:2px;text-align:center;">
                ${weekDays.map(d => `<div style="font-size:12px;color:#666;padding:5px;">${d}</div>`).join('')}
            </div>
            <div id="jp-days" style="display:grid;grid-template-columns:repeat(7,1fr);gap:2px;text-align:center;"></div>
        `

        const daysContainer = popup.querySelector('#jp-days') as HTMLElement

        // Empty cells before first day
        for (let i = 0; i < firstDayOfWeek; i++) {
            const empty = document.createElement('div')
            daysContainer.appendChild(empty)
        }

        // Day cells
        for (let d = 1; d <= monthLength; d++) {
            const dayEl = document.createElement('div')
            dayEl.innerText = toPersianNumbers(String(d))
            dayEl.style.cssText = 'padding:8px;cursor:pointer;border-radius:4px;'

            const isSelected = selectedDate && selectedDate.jy === currentYear && selectedDate.jm === currentMonth && selectedDate.jd === d
            const isToday = today.jy === currentYear && today.jm === currentMonth && today.jd === d

            if (isSelected) {
                dayEl.style.background = '#007bff'
                dayEl.style.color = '#fff'
            } else if (isToday) {
                dayEl.style.background = '#e3f2fd'
            }

            dayEl.addEventListener('mouseenter', () => { if (!isSelected) dayEl.style.background = '#f0f0f0' })
            dayEl.addEventListener('mouseleave', () => { if (!isSelected) dayEl.style.background = isToday ? '#e3f2fd' : '' })

            dayEl.addEventListener('click', () => {
                selectedDate = { jy: currentYear, jm: currentMonth, jd: d }
                const gregDate = toGregorian(currentYear, currentMonth, d)
                const jStr = toPersianNumbers(`${currentYear}/${String(currentMonth).padStart(2, '0')}/${String(d).padStart(2, '0')}`)
                input.value = jStr
                popup.style.display = 'none'
                options.onSelect(gregDate, jStr)
            })

            daysContainer.appendChild(dayEl)
        }

        // Navigation
        popup.querySelector('#jp-prev')?.addEventListener('click', () => {
            currentMonth++
            if (currentMonth > 12) { currentMonth = 1; currentYear++ }
            render()
        })

        popup.querySelector('#jp-next')?.addEventListener('click', () => {
            currentMonth--
            if (currentMonth < 1) { currentMonth = 12; currentYear-- }
            render()
        })
    }

    input.addEventListener('click', (e) => {
        e.stopPropagation()
        popup.style.display = popup.style.display === 'none' ? 'block' : 'none'
        if (popup.style.display === 'block') render()
    })

    document.addEventListener('click', () => { popup.style.display = 'none' })
    popup.addEventListener('click', (e) => e.stopPropagation())

    return {
        el: container,
        getGregorianDate: () => selectedDate ? toGregorian(selectedDate.jy, selectedDate.jm, selectedDate.jd) : null,
        getJalaaliDate: () => selectedDate
    }
}
